import { memo, type ReactNode } from 'react';
import { Section, Sheet } from '@/components/ui';
import { TAJWEED_CLASS_TO_KEY, TAJWEED_RULES } from './fonts';

/* quran.com's text_uthmani_tajweed is HTML like
     ذ<tajweed class=madda_normal>َٲ</tajweed>لِكَ … <span class=end>٢</span>
   It is parsed into React elements, never injected as HTML: only these two
   tag shapes are recognised and everything else is treated as plain text. */
const TOKEN = /<tajweed class=["']?([a-z_]+)["']?>([^<]*)<\/tajweed>|<span class=["']?end["']?>([^<]*)<\/span>|<[^>]*>|([^<]+)/g;

/* The tajweed text writes some small (dagger) alifs as U+0672, which the
   Uthmani text font has no glyph for (a dotted circle shows, e.g. in
   ٱلسَّمَٰوَٰتِ). The plain Uthmani text uses U+0670 there — use that. */
const normalise = (s: string) => s.replace(/ٲ/g, 'ٰ');

/* Some rules in the markup wrap only marks — a small alif and maddah (ـٰٓ),
   a sukūn — so only a sliver changed colour and it was hard to see (e.g.
   لِلْمَلَـٰٓئِكَةِ, 2:30). A rule that starts with marks takes the letter
   they sit on, as the printed tajweed muṣḥaf colours the letter with its sign. */
const STARTS_WITH_MARK = /^[\u0640\u064B-\u065F\u0670\u06D6-\u06ED]/;
const LAST_CLUSTER = /[\u0621-\u063A\u0641-\u064A\u0671-\u06D3][\u064B-\u065F\u0670\u06D6-\u06ED]*$/;

export function parseTajweed(html: string): { text: string; rule?: string; end?: boolean }[] {
  const out: { text: string; rule?: string; end?: boolean }[] = [];
  for (const m of normalise(html).matchAll(TOKEN)) {
    if (m[1] != null) {
      const part = { text: m[2], rule: TAJWEED_CLASS_TO_KEY[m[1]] };
      const prev = out[out.length - 1];
      const take = prev && !prev.rule && !prev.end && STARTS_WITH_MARK.test(part.text) ? LAST_CLUSTER.exec(prev.text) : null;
      if (take) {
        prev.text = prev.text.slice(0, take.index);
        part.text = take[0] + part.text;
        if (!prev.text) out.pop();
      }
      out.push(part);
    }
    else if (m[3] != null) out.push({ text: m[3], end: true });
    else if (m[4] != null) out.push({ text: m[4] });
  }
  return out;
}

type Part = { text: string; rule?: string; end?: boolean };

/* Split tajweed markup into words (keeping each letter's rule), so the
   reciter's word timings can highlight word by word. A rule span can cross a
   space (idghām joins two words) — it is split and each half keeps the rule. */
export function tajweedWords(html: string): { words: Part[][]; end: string | null } {
  const words: Part[][] = [[]];
  let end: string | null = null;
  for (const p of parseTajweed(html)) {
    if (p.end) { end = p.text; continue; }
    p.text.split(/(\s+)/).forEach((chunk) => {
      if (!chunk) return;
      if (/^\s+$/.test(chunk)) { if (words[words.length - 1].length) words.push([]); return; }
      words[words.length - 1].push({ text: chunk, rule: p.rule });
    });
  }
  if (!words[words.length - 1].length) words.pop();
  // A standalone pause mark (ۚ ۖ ۗ …) between spaces belongs to the word before it.
  // A leading one (۞ rubʿ al-ḥizb at the start of an āyah) joins the word after it.
  const merged: Part[][] = [];
  let lead: Part[] = [];
  for (const w of words) {
    const hasLetter = w.some((part) => /[ء-يٱ-ۓ]/.test(part.text));
    if (hasLetter) { merged.push(lead.length ? [...lead, { text: ' ' }, ...w] : w); lead = []; }
    else if (merged.length) merged[merged.length - 1].push({ text: ' ' }, ...w);
    else lead.push(...w);
  }
  return { words: merged, end };
}

const renderParts = (parts: Part[]) => parts.map((p, i) => (p.rule ? <span key={i} className={`tj-${p.rule}`}>{p.text}</span> : <span key={i}>{p.text}</span>));

/** Tajweed words, individually highlightable — one markup string per word (alignTajweedWords). */
export const TajweedWords = memo(function TajweedWords({ words, joined, activeWord, ayahOn, onWord }: {
  words: string[]; joined?: Set<number>; activeWord: number | null; ayahOn: boolean; onWord?: (position: number) => void;
}) {
  return (
    <>
      {words.map((w, i) => (
        <span key={i} className={`qword ${ayahOn ? 'ayah-on' : ''} ${activeWord === i + 1 ? 'word-on' : ''}`} onClick={() => onWord?.(i + 1)}>
          {renderParts(parseTajweed(w))}{joined?.has(i + 1) ? '' : ' '}
        </span>
      ))}
    </>
  );
});

export const TajweedText = memo(function TajweedText({ html }: { html: string }) {
  const parts: ReactNode[] = parseTajweed(html).map((p, i) =>
    p.end ? <span key={i} className="ayah-num">{p.text}</span>
      : p.rule ? <span key={i} className={`tj-${p.rule}`}>{p.text}</span>
        : p.text,
  );
  return <>{parts}</>;
});

export function TajweedLegend({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="Tajweed colours">
      <p className="px-6 pb-3 text-[14px] text-muted">The colour shows which tajweed rule applies to a letter. One count (ḥarakah) is about the time it takes to fold or unfold a finger.</p>
      <Section>
        {TAJWEED_RULES.map((r) => (
          <div key={r.key} className="row items-start">
            <span className="mt-1 h-4 w-4 shrink-0 rounded-full" style={{ background: `var(--tj-${r.key})` }} aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className={`font-semibold tj-${r.key}`}>{r.name}</span>
                <span className="font-arabic text-[17px] text-muted">{r.ar}</span>
              </span>
              <span className="block text-[13px] leading-snug text-muted">{r.desc}</span>
            </span>
          </div>
        ))}
      </Section>
    </Sheet>
  );
}
